import { contextOptionLabel, type StructuredContext } from '@blueprint/domain'
import type { ReactNode } from 'react'
import styles from './context.module.css'

/** Outline glyphs on a 24px grid, drawn with the current text colour. */
const COMPONENT_ICONS: Record<string, ReactNode> = {
  web_app: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18" />
    </>
  ),
  api: (
    <path d="M8 4C6 4 5 5 5 7v2c0 1.5-1 2.5-2 3 1 .5 2 1.5 2 3v2c0 2 1 3 3 3M16 4c2 0 3 1 3 3v2c0 1.5 1 2.5 2 3-1 .5-2 1.5-2 3v2c0 2-1 3-3 3" />
  ),
  service: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  worker: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
    </>
  ),
  library: <path d="M4 4h4v16H4zM10 4h4v16h-4zM15 5l4-1 3 15-4 1z" />,
  cli: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m7 9 3 3-3 3M12 15h5" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </>
  ),
  cache: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
  queue: <path d="M3 6h14M3 12h14M3 18h14M19 9l3 3-3 3" />,
}

const LAYER_ICONS: Record<string, ReactNode> = {
  presentation: (
    <>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  api: <path d="M4 8h15l-3-3M20 16H5l3 3" />,
  application: (
    <>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </>
  ),
  domain: <path d="M12 2l9 5v10l-9 5-9-5V7zM3 7l9 5 9-5M12 12v10" />,
  infrastructure: (
    <>
      <rect x="3" y="3" width="18" height="7" rx="1" />
      <rect x="3" y="14" width="18" height="7" rx="1" />
      <path d="M7 6.5h.01M7 17.5h.01" />
    </>
  ),
  data: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1" />
      <path d="M3 10h18M3 15h18M9 4v16" />
    </>
  ),
  integration: (
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
  ),
}

function Glyph({ label, className, children }: { label: string; className: string | undefined; children: ReactNode }) {
  return (
    <svg
      className={`${styles.archIcon} ${className ?? ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {children}
    </svg>
  )
}

/**
 * The component type and architecture layer written on this element itself — not
 * inherited ones — as icons for a title. Renders nothing when neither is set.
 */
export function ArchitectureIcons({ context }: { context: StructuredContext | undefined }) {
  const componentType = context?.componentType ?? ''
  const layer = context?.architectureLayer ?? ''
  const component = componentType ? COMPONENT_ICONS[componentType] : undefined
  const layerIcon = layer ? LAYER_ICONS[layer] : undefined
  if (!component && !layerIcon) return null
  return (
    <span className={styles.archIcons}>
      {component ? (
        <Glyph label={`Component type: ${contextOptionLabel(componentType)}`} className={styles.archComponent}>
          {component}
        </Glyph>
      ) : null}
      {layerIcon ? (
        <Glyph label={`Architecture layer: ${contextOptionLabel(layer)}`} className={styles.archLayer}>
          {layerIcon}
        </Glyph>
      ) : null}
    </span>
  )
}
